import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { StatusBadge } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserCheck, Check, X, Clock, BookOpen, Home } from "lucide-react";
import { toast } from "sonner";

export default function Enrollments() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [units, setUnits] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => {
    const url = filter === "all" ? "/enrollments" : `/enrollments?program_id=${filter}`;
    api.get(url).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => { load(); }, [filter]);
  useEffect(() => { if (user.role !== "student") { api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {}); api.get("/units").then((r) => setUnits(r.data)).catch(() => {}); } }, []);

  const act = async (id, status) => {
    try { await api.put(`/enrollments/${id}`, { status }); toast.success("تم التحديث"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const linkUnit = async (studentId, unitId) => {
    try { await api.put(`/users/${studentId}`, { unit_id: unitId }); toast.success("تم ربط الطالب بالوحدة"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (rows === null) return <PageLoader />;

  if (user.role === "student") {
    return (
      <>
        <PageHeader title="برامجي" subtitle="البرامج التي سجّلت فيها" />
        {rows.length === 0 ? <EmptyState icon={BookOpen} title="لم تسجّل في أي برنامج بعد" desc="تصفّح البرامج المتاحة وسجّل الآن." /> : (
          <div className="stagger grid gap-4">
            {rows.map((e) => (
              <div key={e.id} data-testid={`enroll-row-${e.id}`} className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-white p-5 soft-shadow">
                <div>
                  <p className="font-heading font-bold text-navy">{e.program_name}</p>
                  {e.attendance && <p className="mt-1 text-sm text-slate-500">الساعات: {e.attendance.attended_hours}/{e.attendance.total_hours} — الحضور {e.attendance.percent}%</p>}
                </div>
                <StatusBadge status={e.status} />
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <PageHeader title="إدارة التسجيلات" subtitle="قبول ورفض ووضع الطلاب في قائمة الانتظار"
        action={
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="enroll-filter" className="w-56 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل البرامج</SelectItem>
              {programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        }
      />
      {rows.length === 0 ? <EmptyState icon={UserCheck} title="لا توجد تسجيلات" /> : (
        <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white soft-shadow">
          <table className="w-full text-start text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="p-4 text-start font-semibold">الطالب</th>
                <th className="p-4 text-start font-semibold">البرنامج</th>
                <th className="p-4 text-start font-semibold">الحضور</th>
                <th className="p-4 text-start font-semibold">الوحدة السكنية</th>
                <th className="p-4 text-start font-semibold">الحالة</th>
                <th className="p-4 text-start font-semibold">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} data-testid={`enroll-row-${e.id}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="p-4">
                    <p className="font-semibold text-navy">{e.student_name}</p>
                    <p className="text-xs text-slate-400">{e.university_status}</p>
                  </td>
                  <td className="p-4 text-slate-600">{e.program_name}</td>
                  <td className="p-4 text-slate-600">{e.attendance ? `${e.attendance.percent}%` : "—"}</td>
                  <td className="p-4">
                    <Select value={e.student_unit_id || "none"} onValueChange={(v) => linkUnit(e.student_id, v === "none" ? "" : v)}>
                      <SelectTrigger data-testid={`unit-select-${e.id}`} className="w-40 rounded-lg"><SelectValue placeholder="بدون وحدة" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">بدون وحدة</SelectItem>
                        {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4"><StatusBadge status={e.status} /></td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <Button data-testid={`approve-${e.id}`} onClick={() => act(e.id, "approved")} size="sm" className="rounded-lg bg-emerald-600 hover:bg-emerald-700 h-8 w-8 p-0"><Check className="h-4 w-4" /></Button>
                      <Button data-testid={`reject-${e.id}`} onClick={() => act(e.id, "rejected")} size="sm" variant="outline" className="rounded-lg border-red-200 text-red-500 h-8 w-8 p-0"><X className="h-4 w-4" /></Button>
                      <Button data-testid={`waitlist-${e.id}`} onClick={() => act(e.id, "waitlist")} size="sm" variant="outline" className="rounded-lg border-purple-200 text-purple-600 h-8 w-8 p-0"><Clock className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
